// ** React Imports
import React, { useState, Fragment, useMemo } from "react";

// ** MUI Imports
import {
  Box,
  Badge,
  Button,
  IconButton,
  Typography,
  Checkbox,
  FormControlLabel,
  Divider
} from "@mui/material";
import { useTheme, styled } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import MuiMenu from "@mui/material/Menu";
import MuiMenuItem from "@mui/material/MenuItem";

// ** Icon Imports
import Icon from "../../../components/icon";

// ** Third Party Components
import PerfectScrollbarComponent from "react-perfect-scrollbar";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import dayjs from "dayjs";

// ** Custom Components Imports
import CustomChip from "../../../components/mui/chip";
import CustomAvatar from "../../../components/mui/avatar";

// ** Util Import
import { getInitials } from "../../../components/utils/get-initials";

// ** Logic Imports
import { useNotificationLogic } from "./logic/notification-logic";
import { useSettings } from 'components/hooks/useSettings'

// ** Styled Menu component
const Menu = styled(MuiMenu)(({ theme }) => ({
  "& .MuiMenu-paper": {
    width: 580,
    overflow: "hidden",
    marginTop: theme.spacing(4.25),
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  "& .MuiMenu-list": {
    padding: 0,
    "& .MuiMenuItem-root": {
      marginLeft: theme.spacing(4),
      marginBottom: theme.spacing(2),
      borderRadius: theme.spacing(2),
      padding: theme.spacing(3, 4), // (y, x)
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    },
  },
}));

// ** Styled MenuItem component
const MenuItem = styled(MuiMenuItem)(({ theme }) => ({
  borderTop: "1px solid #E1E1E1",
  borderRight: "1px solid #E1E1E1",
  borderBottom: "1px solid #E1E1E1",
  borderLeft: "4px solid #23A26D",
  // borderRadius: `${theme.spacing(2)} !important`,
  // '&:not(:last-of-type)': {
  //   borderBottom: `1px solid ${theme.palette.divider}`
  // }
}));

// ** Styled PerfectScrollbar component
const PerfectScrollbar = styled(PerfectScrollbarComponent)({
  maxHeight: 349,
});

// ** Styled Avatar component
const Avatar = styled(CustomAvatar)({
  width: 38,
  height: 38,
  fontSize: "1.125rem",
});

// ** Styled component for the title in MenuItems
const MenuItemTitle = styled(Typography)({
  fontWeight: 500,
  flex: "1 1 100%",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
});

// ** Styled component for the subtitle in MenuItems
const MenuItemSubtitle = styled(Typography)({
  flex: "1 1 100%",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
});

const ScrollWrapper = ({ children, hidden }) => {
  hidden = true;
  if (hidden) {
    return (
      <Box sx={{ maxHeight: 349, overflowY: "auto", overflowX: "hidden" }}>
        {children}
      </Box>
    );
  } else {
    return (
      <PerfectScrollbar
        options={{ wheelPropagation: false, suppressScrollX: true }}
      >
        {children}
      </PerfectScrollbar>
    );
  }
};

const NotificationDropdown = React.memo(() => {
  // ** Props
  const { settings } = useSettings();
  const { NotificationProps } = useNotificationLogic();
  const { 
    notifications,
    unreadNotifications,
    selectedButton,
    selectedNotifications,
    isAllSelected,
    handleFilterChange,
    handleSelectNotification,
    handleSelectAll,
    handleMarkAsRead
  } = NotificationProps;

  // ** States
  const [anchorEl, setAnchorEl] = useState(null);

  // ** Hook
  const hidden = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const theme = useTheme();

  // ** Vars
  const { direction, mode } = settings;
  const filterButtons = [{ name: "Unread" }, { name: "Read" }];

  const handleDropdownOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
  };

  // const RenderAvatar = ({ notification }) => {
  //   return <Avatar skin="light">AB</Avatar>;
  // };

  const renderedFilterButtons = useMemo(() => 
    filterButtons.map((button, index) => {
      const isSelected = selectedButton === index;
      const isDarkMode = theme.palette.mode === 'dark';
      
      return (
        <Button
          key={index}
          color={isSelected ? (isDarkMode ? "secondary" : "main") : "secondary"}
          variant={isSelected ? (isDarkMode ? "outlined" : "contained") : "text"}
          onClick={() => handleFilterChange(index)}
          sx={{
            px: 10,
            backgroundColor: isDarkMode 
              ? (isSelected ? 'rgba(255, 255, 255, 0.08)' : 'inherit') 
              : (isSelected ? "#fff" : "#F5F7FA"),
            color: isSelected ? "text.primary" : "text.secondary",
            "&:hover": {
              backgroundColor: isDarkMode 
                ? (isSelected ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.04)')
                : (isSelected ? "#fff" : "#E8ECF2"),
              color: "inherit",
            },
            border: isDarkMode && isSelected ? '1px solid' : 'none',
            borderColor: isDarkMode && isSelected ? 'secondary.main' : 'transparent'
          }}
          size="small"
        >
          {button.name}
        </Button>
      );
    }), [selectedButton, handleFilterChange, theme.palette.mode, filterButtons]);

  const notificationItems = useMemo(() => 
    notifications?.map((notification, index) => (
      <MenuItem
        key={notification.userNotificationID || index}
        disableRipple
        disableTouchRipple
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {selectedButton === 0 && (
            <FormControlLabel 
              control={
                <Checkbox 
                  color="success" 
                  checked={selectedNotifications.includes(notification.userNotificationID)}
                  onChange={() => handleSelectNotification(notification.userNotificationID)}
                  onClick={(e) => e.stopPropagation()}
                />
              } 
            />
          )}
          <Box sx={{ display: "flex", alignItems: "start", gap: 2 }}>
            <Box sx={{
              flex: "1 1",
              display: "flex",
              overflow: "hidden",
              flexDirection: "column",
              flexWrap: "wrap",
            }}>
              <MenuItemTitle>{notification.title}</MenuItemTitle>
              <MenuItemSubtitle
                variant="body2"
                sx={{
                  whiteSpace: "wrap",
                  "& a": { color: "#23A26D", textDecoration: "none" },
                }}
                dangerouslySetInnerHTML={{ __html: notification.description }}
              />
              <Typography variant="body2" sx={{ color: "text.disabled" }}>
                {notification.createDate ? dayjs(notification.createDate).format('MM/DD/YYYY hh:mm A') : ''}
              </Typography>
            </Box>
          </Box>
        </Box>
      </MenuItem>
    )), [notifications, selectedButton, selectedNotifications, handleSelectNotification]
  );

  return (
    <Fragment>
      <IconButton
        color="inherit"
        aria-haspopup="true"
        onClick={handleDropdownOpen}
        aria-controls="customized-menu"
      >
        <Badge
          color="error"
          variant="dot"
          invisible={!unreadNotifications?.length}
          sx={{
            "& .MuiBadge-badge": {
              top: 4,
              right: 4,
              boxShadow: (theme) => `0 0 0 2px ${theme.palette.background.paper}`,
            },
          }}
        >
          <Icon fontSize="1.625rem" icon="tabler:bell" />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleDropdownClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: direction === "ltr" ? "right" : "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: direction === "ltr" ? "right" : "left",
        }}
      >
        <Box sx={{
          cursor: "default",
          userSelect: "auto",
          backgroundColor: "transparent !important",
          px: theme.spacing(4),
          paddingTop: theme.spacing(4),
          paddingBottom: theme.spacing(2),
        }}>
          <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 2 }}>
            <Typography variant="h5" sx={{ cursor: "text" }}>Notifications</Typography>
            <CustomChip skin="dark" size="small" color="error" label={`${notifications?.length}`} />
          </Box>
          <Box sx={{
            display: "inline-flex",
            alignItems: "center",
            mt: 4,
            backgroundColor: theme.palette.background.default,
            border: '1px solid #F5F7FA',
            p: 2,
            borderRadius: 2,
          }}>
            {renderedFilterButtons}
          </Box>
          {selectedButton === 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <FormControlLabel
                control={
                  <Checkbox 
                    color="success" 
                    checked={isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    disabled={notifications.length === 0}
                  />
                }
                label="Select All"
              />
              <Button
                variant='outlined'
                color='main'
                size='small'
                onClick={handleMarkAsRead}
                disabled={selectedNotifications.length === 0}
              >
                Mark As Read
              </Button>
            </Box>
          )}
        </Box>
        {notifications && notifications.length > 0 ? (
          <ScrollWrapper hidden={hidden}>
            {notificationItems}
          </ScrollWrapper>
        ) : (
          <Box sx={{ textAlign: "center", py: 8 }}>There's no notifications</Box>
        )}
      </Menu>
    </Fragment>
  );
});

export default NotificationDropdown;
